import React, { useState, useEffect } from 'react';import ReactDOM from 'react-dom';import API_BASE_URL from '../config';import styles from '../css-isolated/sa_edituser.module.css';
import authFetch from '../utils/authFetch';

function SaResetUserPassword({ isDarkMode }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    userType: '',
    email: ''
  });
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [pendingDeleteUser, setPendingDeleteUser] = useState(null);
  const [isTogglingDisable, setIsTogglingDisable] = useState({});
  const [showDisableConfirmModal, setShowDisableConfirmModal] = useState(false);
  const [pendingDisableUser, setPendingDisableUser] = useState(null);

  // Fetch all users from the database
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setUsers(data.data);
      } else {
        setErrors({ general: data.message || 'Failed to fetch users.' });
      }
    } catch (error) {
      console.error('Fetch users error:', error);
      setErrors({ general: 'Network error. Please check your connection.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setFormData({
      firstName: user.first_name,
      middleName: user.middle_name || '',
      lastName: user.last_name,
      userType: user.user_type,
      email: user.email
    });
    setErrors({});
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      middleName: '',
      lastName: '',
      userType: '',
      email: ''
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.userType) {
      newErrors.userType = 'User type is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateUser = async (userId) => {
    if (!validateForm()) {
      return;
    }

    // Check if trying to change the last admin's role
    const currentUser = users.find(user => user.id === userId);
    const adminUsers = users.filter(user => user.user_type === 'admin');
    
    if (currentUser?.user_type === 'admin' && 
        formData.userType !== 'admin' && 
        adminUsers.length === 1) {
      setModalTitle('Cannot Change Admin Role');
      setModalMessage('You cannot change the role of the last admin user. There must be at least one admin in the system.');
      setShowErrorModal(true);
      return;
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          middle_name: formData.middleName || null,
          last_name: formData.lastName,
          user_type: formData.userType,
          email: formData.email
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          const backendErrors = {};
          Object.keys(data.errors).forEach(key => {
            const fieldMap = {
              'first_name': 'firstName',
              'middle_name': 'middleName',
              'last_name': 'lastName',
              'user_type': 'userType',
              'email': 'email'
            };
            const reactKey = fieldMap[key] || key;
            backendErrors[reactKey] = data.errors[key][0];
          });
          setErrors(backendErrors);
          return;
        }
        throw new Error(data.message || 'Failed to update user');
      }

      setModalTitle('User Updated Successfully!');
      setModalMessage(`User ${formData.firstName} ${formData.lastName} has been successfully updated.`);
      setShowSuccessModal(true);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Update user error:', error);
      setModalTitle('Error Updating User');
      setModalMessage(error.message || 'Failed to update user. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleDeleteUser = (userId, userEmail, userType) => {
    // Check if this is the last admin
    const adminUsers = users.filter(user => user.user_type === 'admin');
    if (userType === 'admin' && adminUsers.length === 1) {
      setModalTitle('Cannot Delete Admin');
      setModalMessage('You cannot delete the last admin user. There must be at least one admin in the system.');
      setShowErrorModal(true);
      return;
    }
    
    setPendingDeleteUser({ userId, userEmail });
    setShowConfirmModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!pendingDeleteUser) return;
    
    const { userId, userEmail } = pendingDeleteUser;
    setShowConfirmModal(false);
    setIsDeleting(prev => ({ ...prev, [userId]: true }));
    setErrors({});

    try {
      const response = await authFetch(`${API_BASE_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setModalTitle('User Deleted Successfully!');
        setModalMessage(`User ${userEmail} has been successfully deleted.`);
        setShowSuccessModal(true);
        // Refresh the users list
        fetchUsers();
      } else {
        setModalTitle('Error Deleting User');
        setModalMessage(data.message || 'Failed to delete user. Please try again.');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Delete user error:', error);
      setModalTitle('Error Deleting User');
      setModalMessage('Network error. Please check your connection and try again.');
      setShowErrorModal(true);
    } finally {
      setIsDeleting(prev => ({ ...prev, [userId]: false }));
      setPendingDeleteUser(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setPendingDeleteUser(null);
  };

  const handleDisableUser = (user) => {
    setPendingDisableUser(user);
    setShowDisableConfirmModal(true);
  };

  const confirmDisableUser = async () => {
    if (!pendingDisableUser) return;
    const { id, email } = pendingDisableUser;
    setShowDisableConfirmModal(false);
    setIsTogglingDisable(prev => ({ ...prev, [id]: true }));
    try {
      const response = await authFetch(`${API_BASE_URL}/api/users/${id}/toggle-disable`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const action = data.acc_disable ? 'disabled' : 'enabled';
        setModalTitle(`Account ${action.charAt(0).toUpperCase() + action.slice(1)}`);
        setModalMessage(`Account for ${email} has been ${action} successfully.`);
        setShowSuccessModal(true);
        fetchUsers();
      } else {
        setModalTitle('Error');
        setModalMessage(data.message || 'Failed to update account status.');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Toggle disable error:', error);
      setModalTitle('Error');
      setModalMessage('Network error. Please check your connection and try again.');
      setShowErrorModal(true);
    } finally {
      setIsTogglingDisable(prev => ({ ...prev, [id]: false }));
      setPendingDisableUser(null);
    }
  };

  const cancelDisableToggle = () => {
    setShowDisableConfirmModal(false);
    setPendingDisableUser(null);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.middle_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`${styles.contentWrapper} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
      {/* Main Form Section */}
      <div className={styles.formSection}>
        
        {/* Section Header with Icon */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 12C21 12 18 18 12 18C6 18 3 12 3 12C3 12 6 6 12 6C18 6 21 12 21 12Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div>
            <h3 className={styles.sectionTitle}>User Management</h3>
            <div className={styles.sectionAccent}></div>
          </div>
        </div>

        {/* Error Alert */}
        {errors.general && (
          <div className={styles.errorAlert}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            {errors.general}
          </div>
        )}

        {/* Search and Controls */}
        <div className={styles.controlsSection}>
          <div className={styles.searchContainer}>
            <div className={styles.searchInputContainer}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          </div>
          
          <button
            onClick={fetchUsers}
            className={styles.refreshButton}
            disabled={isLoading}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12C3 12 3 4.5 10.5 4.5C18 4.5 21 12 21 12" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 12C21 12 21 19.5 13.5 19.5C6 19.5 3 12 3 12" stroke="currentColor" strokeWidth="2"/>
              <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="2"/>
              <path d="M17 16L21 12L17 8" stroke="currentColor" strokeWidth="2"/>
            </svg>
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* Users Table */}
        <div className={styles.tableContainer}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <svg width="40" height="40" className={styles.loadingSpinner} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <p>Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <p>No users found</p>
              {searchTerm && <p>Try adjusting your search criteria</p>}
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.usersTable}>
                <thead>
                  <tr>
                    <th>User Type</th>
                    <th>First Name</th>
                    <th>Middle Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Account Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    editingUser === user.id ? (
                      <tr key={user.id} className={styles.editingRow}>
                        <td className={styles.userTypeCell}>
                          <select
                            name="userType"
                            value={formData.userType}
                            onChange={handleInputChange}
                            className={`${styles.editInput} ${errors.userType ? styles.error : ''}`}
                          >
                            <option value="">Select type</option>
                            <option value="unit_nurse">Unit Nurse</option>
                            <option value="chief_nurse">Chief Nurse</option>
                            <option value="warden">Warden</option>
                            <option value="jail_officer">Jail Officer</option>
                            <option value="admin">Admin</option>
                          </select>
                          {errors.userType && <span className={styles.errorText}>{errors.userType}</span>}
                        </td>
                        <td>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className={`${styles.editInput} ${errors.firstName ? styles.error : ''}`}
                            placeholder="First name"
                          />
                          {errors.firstName && <span className={styles.errorText}>{errors.firstName}</span>}
                        </td>
                        <td>
                          <input
                            type="text"
                            name="middleName"
                            value={formData.middleName}
                            onChange={handleInputChange}
                            className={styles.editInput}
                            placeholder="Middle name"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className={`${styles.editInput} ${errors.lastName ? styles.error : ''}`}
                            placeholder="Last name"
                          />
                          {errors.lastName && <span className={styles.errorText}>{errors.lastName}</span>}
                        </td>
                        <td>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={`${styles.editInput} ${errors.email ? styles.error : ''}`}
                            placeholder="Email"
                          />
                          {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                        </td>
                        <td className={styles.statusCell}>—</td>
                        <td className={styles.actionCell}>
                          <div className={styles.actionButtons}>
                            <button
                              onClick={() => handleUpdateUser(user.id)}
                              className={styles.saveButton}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2"/>
                                <polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" strokeWidth="2"/>
                                <polyline points="7 3 7 8 15 8" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className={styles.cancelButton}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
                                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={user.id} className={styles.tableRow}>
                      <td className={styles.userTypeCell}>
                        <span className={`${styles.typeBadge} ${styles[`type${user.user_type?.replace('_', '')}` || 'typeDefault']}`}>
                          {user.user_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                        </span>
                      </td>
                      <td className={styles.nameCell}>{user.first_name}</td>
                      <td className={styles.nameCell}>{user.middle_name || '-'}</td>
                      <td className={styles.nameCell}>{user.last_name}</td>
                      <td className={styles.emailCell}>{user.email}</td>
                      <td className={styles.statusCell}>
                        <span className={`${styles.statusBadge} ${user.acc_disable ? styles.statusDisabled : styles.statusActive}`}>
                          {user.acc_disable ? 'Disabled' : 'Active'}
                        </span>
                      </td>
                      <td className={styles.actionCell}>
                        <div className={styles.actionButtons}>
                          <button
                            onClick={() => handleEditUser(user)}
                            className={styles.editButton}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.43741 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.email, user.user_type)}
                            disabled={isDeleting[user.id]}
                            className={`${styles.deleteButton} ${isDeleting[user.id] ? styles.deleting : ''}`}
                          >
                            {isDeleting[user.id] ? (
                              <>
                                <svg width="14" height="14" className={styles.buttonSpinner} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                Deleting...
                              </>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Delete
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleDisableUser(user)}
                            disabled={isTogglingDisable[user.id]}
                            className={`${styles.disableButton} ${user.acc_disable ? styles.disableButtonEnable : ''}`}
                          >
                            {isTogglingDisable[user.id] ? (
                              <>
                                <svg width="14" height="14" className={styles.buttonSpinner} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                Updating...
                              </>
                            ) : user.acc_disable ? (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                Enable
                              </>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                  <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                                Disable
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Info */}
        {!isLoading && filteredUsers.length > 0 && (
          <div className={styles.summarySection}>
            <div className={styles.summaryStats}>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>{filteredUsers.length}</span>
                <span className={styles.statLabel}>Total Users</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>
                  {filteredUsers.filter(user => user.last_login_at !== null).length}
                </span>
                <span className={styles.statLabel}>Active Users</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>
                  {filteredUsers.filter(user => user.last_login_at === null).length}
                </span>
                <span className={styles.statLabel}>Never Logged In</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Disable/Enable Confirmation Modal */}
      {showDisableConfirmModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${!isDarkMode ? 'lightMode' : ''}`} onClick={cancelDisableToggle}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>{pendingDisableUser?.acc_disable ? 'Enable Account' : 'Disable Account'}</h3>
            <p>Are you sure you want to <strong>{pendingDisableUser?.acc_disable ? 'enable' : 'disable'}</strong> the account for:</p>
            <p><strong>{pendingDisableUser?.email}</strong></p>
            {!pendingDisableUser?.acc_disable && (
              <p className={styles.warningText}>This will prevent the user from logging in.</p>
            )}
            <div className={styles.confirmActions}>
              <button className={styles.cancelBtn} onClick={cancelDisableToggle}>
                Cancel
              </button>
              <button
                className={pendingDisableUser?.acc_disable ? styles.enableConfirmBtn : styles.confirmBtn}
                onClick={confirmDisableUser}
              >
                {pendingDisableUser?.acc_disable ? 'Enable' : 'Disable'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${!isDarkMode ? 'lightMode' : ''}`} onClick={cancelDelete}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete the user: <strong>{pendingDeleteUser?.userEmail}</strong>?</p>
            <p className={styles.warningText}>This action cannot be undone!</p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelBtn} onClick={cancelDelete}>
                Cancel
              </button>
              <button className={styles.confirmBtn} onClick={confirmDeleteUser}>
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Success Modal */}
      {showSuccessModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${!isDarkMode ? 'lightMode' : ''}`} onClick={() => setShowSuccessModal(false)}>
          <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>{modalTitle}</h3>
            <p>{modalMessage}</p>
            <button className={styles.successBtn} onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Error Modal */}
      {showErrorModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${!isDarkMode ? 'lightMode' : ''}`} onClick={() => setShowErrorModal(false)}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>{modalTitle}</h3>
            <p>{modalMessage}</p>
            <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default SaResetUserPassword;